/**
 * @fileoverview Normalized error type for the entire data layer.
 *
 * Every API call funnels through `normalizeApiError` so downstream code
 * (services, hooks, components) sees one consistent shape instead of
 * checking `error.response?.data?.error || error.message || ...` everywhere.
 */

import type { AxiosError } from 'axios'

export type ApiErrorKind =
  | 'validation'    // 4xx with field-level details (Zod / backend schemas)
  | 'auth'          // 401 / 403
  | 'not_found'     // 404
  | 'conflict'      // 409
  | 'rate_limited'  // 429
  | 'server'        // 5xx
  | 'network'       // request never reached the server
  | 'cancelled'     // client aborted (timeout / unmount)
  | 'unknown'

export interface ValidationDetail {
  field: string
  message: string
}

export class ApiError extends Error {
  readonly kind: ApiErrorKind
  readonly status?: number
  readonly details: ValidationDetail[]
  readonly raw: unknown
  /** True iff `message` was extracted from a real backend response body
   *  (`response.data.error` / `response.data.message`). Lets
   *  `getErrorMessage` distinguish "the server told us what went wrong"
   *  (worth showing the user) from "we made up a generic message"
   *  (prefer the caller's action-specific fallback). */
  readonly bodySourced: boolean

  constructor(opts: {
    message: string
    kind: ApiErrorKind
    status?: number
    details?: ValidationDetail[]
    raw?: unknown
    bodySourced?: boolean
  }) {
    super(opts.message)
    this.name = 'ApiError'
    this.kind = opts.kind
    this.status = opts.status
    this.details = opts.details ?? []
    this.raw = opts.raw
    this.bodySourced = opts.bodySourced ?? false
  }
}

interface BackendErrorBody {
  /** Either a string ("Already assigned") or a nested object
   *  (`{ message: "Already assigned" }`) depending on which backend
   *  service replied. Both shapes are read in `pickBodyMessage` below. */
  error?: string | { message?: string }
  message?: string
  details?: Array<{ field?: string; path?: string; message: string }>
}

/** Read the human-readable message out of a backend error body, tolerating
 *  the two shapes that exist across services: `error: "..."` (string) and
 *  `error: { message: "..." }` (nested object). Returns undefined when no
 *  usable string is present so the caller can fall through. */
function pickBodyMessage(body: BackendErrorBody): string | undefined {
  if (typeof body.error === 'string') return body.error
  if (body.error && typeof body.error === 'object' && typeof body.error.message === 'string') {
    return body.error.message
  }
  if (typeof body.message === 'string') return body.message
  return undefined
}

export function normalizeApiError(err: unknown): ApiError {
  if (err instanceof ApiError) return err

  // Axios errors carry response + request + config; non-axios errors fall
  // through to a more permissive shape check below so test mocks + any
  // re-thrown axios-shaped objects (without the flag) still extract their
  // backend message instead of collapsing to "Unknown error".
  const axErr = err as AxiosError<BackendErrorBody>
  if (axErr?.isAxiosError) {
    if (axErr.code === 'ERR_CANCELED') {
      return new ApiError({ message: 'Request cancelled', kind: 'cancelled', raw: err })
    }
    if (!axErr.response) {
      // No response means the request never made it (DNS, offline, CORS pre-flight fail).
      return new ApiError({
        message: axErr.message || 'Network error',
        kind: 'network',
        raw: err,
      })
    }

    const status = axErr.response.status
    const body = axErr.response.data ?? {}
    const detailRaw = Array.isArray(body.details) ? body.details : []
    const details: ValidationDetail[] = detailRaw.map((d) => ({
      field: d.field ?? d.path ?? '',
      message: d.message,
    }))
    const bodyMsg = pickBodyMessage(body)
    const message = bodyMsg || axErr.message || `HTTP ${status}`

    let kind: ApiErrorKind = 'unknown'
    if (status === 401 || status === 403) kind = 'auth'
    else if (status === 404) kind = 'not_found'
    else if (status === 409) kind = 'conflict'
    else if (status === 429) kind = 'rate_limited'
    else if (status >= 500) kind = 'server'
    else if (status >= 400) kind = details.length > 0 ? 'validation' : 'unknown'

    return new ApiError({
      message, kind, status, details, raw: err,
      bodySourced: !!bodyMsg,
    })
  }

  // Permissive axios-shaped fallback: some test mocks + a few legacy
  // throwers ship `{ response: { data: { error: '...' } } }` without the
  // `isAxiosError` flag. Honour the same body extraction so callers see
  // the backend's message instead of "Unknown error".
  if (err && typeof err === 'object') {
    const shaped = err as {
      response?: { status?: number; data?: BackendErrorBody }
      message?: string
    }
    if (shaped.response?.data) {
      const status = shaped.response.status ?? 0
      const bodyMsg = pickBodyMessage(shaped.response.data)
      if (bodyMsg) {
        let kind: ApiErrorKind = 'unknown'
        if (status === 401 || status === 403) kind = 'auth'
        else if (status === 404) kind = 'not_found'
        else if (status === 409) kind = 'conflict'
        else if (status === 429) kind = 'rate_limited'
        else if (status >= 500) kind = 'server'
        return new ApiError({
          message: bodyMsg, kind, status: status || undefined, raw: err,
          bodySourced: true,
        })
      }
    }
    if (typeof shaped.message === 'string' && shaped.message.length > 0) {
      return new ApiError({ message: shaped.message, kind: 'unknown', raw: err })
    }
  }

  if (err instanceof Error) {
    return new ApiError({ message: err.message, kind: 'unknown', raw: err })
  }
  return new ApiError({ message: 'Unknown error', kind: 'unknown', raw: err })
}

/**
 * Cheap accessor for the `normalized` field the axios response interceptor
 * (`src/lib/api.ts`) attaches to every rejected response. Falls back to a
 * fresh `normalizeApiError` call when the interceptor hasn't run — e.g. for
 * synthetic errors thrown inside service code or for non-axios errors.
 *
 * Use this in new code instead of digging into `err.response.data.error`.
 */
export function getApiError(err: unknown): ApiError {
  const tagged = err as { normalized?: ApiError } | null | undefined
  if (tagged?.normalized instanceof ApiError) return tagged.normalized
  return normalizeApiError(err)
}

/**
 * Pick the best human-readable error message for a `toast.error(...)` call.
 *
 * Order of preference:
 *   1. The backend's structured `error` / `message` field (the most
 *      informative and locale-aware option, gated on `bodySourced`).
 *   2. The transport-layer message for `cancelled` / `network` errors —
 *      "Network Error" is a useful signal to the user even though it
 *      didn't come from a response body.
 *   3. The caller's action-specific fallback ("Failed to load X") for
 *      everything else: bare `Error.message` ("500"), JS errors, the
 *      synthesized `HTTP <status>` sentinel, or null/undefined input.
 *
 * Always non-empty. Designed to replace per-component
 * `err?.response?.data?.error || err?.message || fallback` glue so every
 * page reads the same structured envelope.
 */
export function getErrorMessage(err: unknown, fallback: string): string {
  const normalized = getApiError(err)
  if (normalized.bodySourced) return normalized.message
  if (normalized.kind === 'cancelled' || normalized.kind === 'network') {
    return normalized.message
  }
  return fallback
}
